import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';

import { DefaultComponentProps, FormStatus, MessageCodes, CreateUserResultTypes } from '../../../types';
import { UserCreateResponse, API_STATUS } from '../../types';
import { getCountryCode, ariaInvalid } from '../../../helpers';
import { isValidEmail, isValidPassword } from '../../../validation';
import { userCreate } from '../../api';

import { CardHeader } from '../../../components/CardHeader';
import { CardBody } from '../../../components/CardBody';
import { Spinner } from '../../../components';

import './style.scss';

interface Props extends DefaultComponentProps {
  accountFirstName: string;
  flowKey: string;
  setView: (resultType: CreateUserResultTypes) => void;
  setTryAgainView: Dispatch<SetStateAction<CreateUserResultTypes>>;
}

export const PinVerified = ({ textConfig, flowKey, accountFirstName, setView, setTryAgainView }: Props) => {

  const {
    defaultTitle,
    ingress,
    emoji,
    emojiLabel,
    formValidationFeedback,
    buttonSubmitText,
    formfieldPasswordLabel,
    formfieldEmailLabel,
    titlePersonalized,
    passwordRules,
    showPasswordLabel,
    hidePasswordLabel } = textConfig.pinVerified;
  const [formStatus, setFormStatus] = useState({
    valid: false,
    status: FormStatus.INITIAL,
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState({
    value: '',
    valid: false,
    show: false,
  });
  const [usingCapsLock, setUsingCapsLock] = useState(false);
  const [usernameTaken, setUsernameTaken] = useState(false);
  const [validationFeedback, setValidationFeedback] = useState({
    email: formValidationFeedback.emailInvalid,
    password: formValidationFeedback.passwordInvalid,
  });
  const [greeting, setGreeting] = useState(defaultTitle);

  useEffect(() => {
    setFormStatus(state => ({
      ...state,
      valid: Object.values(validationFeedback).every((feedback) => feedback === ''),
    }));

    if (accountFirstName) {
      setGreeting(titlePersonalized.replace('{accountFirstName}', accountFirstName));
    }
  }, [validationFeedback, accountFirstName, titlePersonalized]);

  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = {
      flowKey,
      password: password.value,
      countryCode: getCountryCode(),
      email,
    };

    setTryAgainView(CreateUserResultTypes.USER_CREATE);

    if (!formStatus.valid) {
      setFormStatus(state => ({
        ...state,
        status: FormStatus.TRIED_SUBMITTING,
      }));
      return;
    }
    setFormStatus(state => ({
      ...state,
      status: FormStatus.SUBMITTING,
    }));

    userCreate(data).then((result: UserCreateResponse) => {
      switch (result.status) {
        case API_STATUS.FAILURE:
          switch (result.messageCode) {
            case MessageCodes.PASSWORD_TOO_WEAK_6010:
              setValidationFeedback(state => ({ ...state, password: formValidationFeedback.passwordInvalid }));
              setFormStatus(state => ({
                ...state,
                status: FormStatus.TRIED_SUBMITTING,
              }));
              break;
            case MessageCodes.USERNAME_TAKEN_6013:
              setUsernameTaken(true);
              setFormStatus(state => ({
                ...state,
                status: FormStatus.TRIED_SUBMITTING,
              }));
              break;
            default:
              console.warn(result);
              setView(CreateUserResultTypes.USER_CREATE_FAILED);
          }
          break;
        case API_STATUS.NOT_AUTHENTICATED:
          console.warn('API_STATUS.NOT_AUTHENTICATED');
          setView(CreateUserResultTypes.USER_NOT_FOUND);
          break;
        case API_STATUS.SUCCESS:
          setView(CreateUserResultTypes.LEGACY_VERIFY_OFFCODE);
          break;
        case API_STATUS.INTERNAL_FAILURE:
        default:
          console.warn(result);
          setView(CreateUserResultTypes.USER_CREATE_FAILED);
      }
    })
    .catch((error) => {
      console.warn(error);
      setView(CreateUserResultTypes.SYSTEM_ERROR);
    });
  };

  const handleEmail = ({ currentTarget }: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = currentTarget;
    setEmail(value);
    setUsernameTaken(false);
    setValidationFeedback(state => ({ ...state, email: isValidEmail(value) ? '' : formValidationFeedback.emailInvalid }));
  };

  const handlePassword = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    setPassword(state => ({
      ...state,
      value,
      valid: isValidPassword(value),
    }));
    setValidationFeedback(state => ({ ...state, password: isValidPassword(value) ? '' : formValidationFeedback.passwordInvalid }));
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setUsingCapsLock(e.getModifierState('CapsLock'));
  };

  const renderValidationFeeback = (validationFeedback: string) => {
    return formStatus.status === FormStatus.TRIED_SUBMITTING && !formStatus.valid ? (
      <div className="form__field__feedback__text">{validationFeedback}</div>
    ) : null;
  };

  const toggleShowPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    setPassword(state => ({
      ...state,
      show: !state.show,
    }));
  };

  return (
    <>
      {formStatus.valid && formStatus.status === FormStatus.SUBMITTING ?
        <Spinner textConfig={textConfig} /> :
      (
      <>
      <CardHeader title={greeting} ingress={ingress} emoji={emoji} emojiLabel={emojiLabel} />
      <CardBody>
        <form
          onSubmit={handleFormSubmit}
          className={`form ${formStatus.valid && formStatus.status === FormStatus.SUBMITTING ? 'form__submitting' : ''}`}
          noValidate={true}
        >
          <div className="form__field">
            <label htmlFor="email" className="form__field__label">
            {formfieldEmailLabel}
            </label>
            <input
              onChange={handleEmail}
              type="email"
              id="email"
              className="form__field__input"
              value={email}
              autoFocus={true}
              autoComplete="email"
              aria-describedby="emailFeedback"
              aria-invalid={ariaInvalid(formStatus.status, validationFeedback.email)}
            />
            <div className="form__field__feedback" id="emailFeedback">{renderValidationFeeback(validationFeedback.email)}</div>
          </div>

          <p className="form__instructions">{passwordRules}</p>
          <div className="form__field">
            <label htmlFor="password" className="form__field__label">
            {formfieldPasswordLabel}
            </label>
            <div>
              <input
                onChange={handlePassword}
                onKeyUp={handleKeyUp}
                type={password.show ? 'text' : 'password'}
                id="password"
                className="form__field__input"
                aria-describedby="passwordFeedback"
                aria-invalid={ariaInvalid(formStatus.status, validationFeedback.password)}
                value={password.value}
              />
              <button
                id="wp-show-password"
                className="form__password__show btn-link"
                onClick={toggleShowPassword}
                type="button"
              >{password.show ? hidePasswordLabel : showPasswordLabel}
              </button>
            </div>
            <div className="form__field__feedback">
              {password.valid && <span className="form__password__ok">{textConfig.pinVerified.passwordOk}</span>}
              <div id="passwordFeedback">{renderValidationFeeback(validationFeedback.password)}</div>
            </div>
            {usingCapsLock && <div className="alert alert-danger">{textConfig.pinVerified.capsLockWarning}</div>}
          </div>

          <div className="form__field">
            <div className="form__field__submit">
              <button type="submit" id="wp-submit-pending-user" className="form__field__submit__button">
                {buttonSubmitText}
              </button>
            </div>
          </div>
          {usernameTaken && <div className="alert alert-danger form__bottom__alert" role="alert">{textConfig.pinVerified.usernameTaken}</div>}
        </form>
      </CardBody>
      </>
      )}
    </>
  );
};
